class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.stickyClass = 'show';
      this.position = this.dataset.position || 'bottom';
      
      // Bind handlers once to preserve context
      this.onScrollHandler = this.handleScroll.bind(this);
      this.addToCartHandler = this.handleAddToCart.bind(this);
      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.quantityChangeHandler = this.handleQuantityChange.bind(this);
    }
  
    connectedCallback() {
      // Add event listeners
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      
      // Button click handler
      const addToCartButton = this.querySelector('.product-form__submit');
      if (addToCartButton) {
        addToCartButton.addEventListener('click', this.addToCartHandler);
      }
      
      // Listen for custom event
      document.addEventListener('sticky-add-to-cart:click', this.addToCartHandler);
      
      // Set up variant listeners
      this.setupVariantObserver();
      this.setupVariantChangeListeners();
      
      // Set up quantity sync
      this.setupQuantitySync();
      
      // Initialize
      this.updateStickyBarVariant();
      this.handleScroll();
    }
  
    disconnectedCallback() {
      // Remove scroll listener
      window.removeEventListener('scroll', this.onScrollHandler);
      
      // Remove button listener
      const addToCartButton = this.querySelector('.product-form__submit');
      if (addToCartButton) {
        addToCartButton.removeEventListener('click', this.addToCartHandler);
      }
      
      // Remove custom event listener
      document.removeEventListener('sticky-add-to-cart:click', this.addToCartHandler);
      
      // Remove variant listeners
      this.removeVariantListeners();
      
      // Remove quantity listeners
      this.removeQuantityListeners();
      
      // Disconnect observer
      if (this.variantObserver) {
        this.variantObserver.disconnect();
      }
    }
    
    setupVariantObserver() {
      // Use MutationObserver to watch variant ID changes
      const mainForm = document.querySelector('product-form form') ||
                      document.querySelector('form[data-type="add-to-cart-form"]');
      
      if (!mainForm) return;
      
      const variantIdInput = mainForm.querySelector('[name="id"]');
      if (!variantIdInput) return;
      
      this.variantObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            this.updateStickyBarVariant();
            break;
          }
        }
      });
      
      this.variantObserver.observe(variantIdInput, { 
        attributes: true, 
        attributeFilter: ['value'] 
      });
    }
  
    setupVariantChangeListeners() {
      // Common selector for all variant inputs
      const variantInputSelectors = [
        '.product-form__input select',
        '.product-form__input input[type="radio"]',
        '.js-change-variant',
        '.swatch input',
        '.swatch-element',
        '.single-option-selector'
      ].join(', ');
      
      // Add listeners to all variant controls
      document.querySelectorAll(variantInputSelectors).forEach(el => {
        el.addEventListener('change', this.variantChangeHandler);
        
        // Also listen for clicks on radios and swatches
        if ((el instanceof HTMLInputElement && el.type === 'radio') || 
            el.classList.contains('swatch-element') || 
            el.classList.contains('js-change-variant')) {
          el.addEventListener('click', this.variantChangeHandler);
        }
      });
      
      // Listen for common variant change events
      ['variant:change', 'variant:changed', 'property_select:change'].forEach(eventName => {
        document.addEventListener(eventName, this.variantChangeHandler);
      });
      
      // Listen for form changes
      const productForm = document.querySelector('product-form') || 
                         document.querySelector('.product-form');
      if (productForm) {
        productForm.addEventListener('change', this.variantChangeHandler);
      }
    }
    
    removeVariantListeners() {
      // Remove all variant control listeners
      const variantInputSelectors = [
        '.product-form__input select',
        '.product-form__input input[type="radio"]',
        '.js-change-variant',
        '.swatch input',
        '.swatch-element',
        '.single-option-selector'
      ].join(', ');
      
      document.querySelectorAll(variantInputSelectors).forEach(el => {
        el.removeEventListener('change', this.variantChangeHandler);
        
        if ((el instanceof HTMLInputElement && el.type === 'radio') || 
            el.classList.contains('swatch-element') || 
            el.classList.contains('js-change-variant')) {
          el.removeEventListener('click', this.variantChangeHandler);
        }
      });
      
      // Remove event listeners
      ['variant:change', 'variant:changed', 'property_select:change'].forEach(eventName => {
        document.removeEventListener(eventName, this.variantChangeHandler);
      });
      
      // Remove form listeners
      const productForm = document.querySelector('product-form') || 
                         document.querySelector('.product-form');
      if (productForm) {
        productForm.removeEventListener('change', this.variantChangeHandler);
      }
    }
    
    setupQuantitySync() {
      const sectionId = this.dataset.sectionId;
      const mainQuantityInput = document.querySelector(`#Quantity-${sectionId}`);
      const stickyQuantitySelector = this.querySelector('quantity-selector-component');
      const stickyQuantityInput = stickyQuantitySelector ? stickyQuantitySelector.querySelector('input[type="number"]') : null;
      
      if (!(mainQuantityInput instanceof HTMLInputElement) || !(stickyQuantityInput instanceof HTMLInputElement)) return;
      
      // Set initial value
      stickyQuantityInput.value = mainQuantityInput.value;
      
      // Main quantity changes -> update sticky
      mainQuantityInput.addEventListener('change', this.quantityChangeHandler);
      
      // Sticky quantity changes -> update main
      this.stickyQuantityChangeHandler = () => {
        if (mainQuantityInput instanceof HTMLInputElement && stickyQuantityInput instanceof HTMLInputElement) {
          mainQuantityInput.value = stickyQuantityInput.value;
          mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      if (this.stickyQuantityChangeHandler) {
        stickyQuantityInput.addEventListener('change', this.stickyQuantityChangeHandler);
      }
      
      // Handle quantity buttons in sticky bar
      const stickyQuantityButtons = stickyQuantitySelector ? stickyQuantitySelector.querySelectorAll('button[name="minus"], button[name="plus"]') : [];
      this.stickyQuantityButtonHandler = () => {
        // Small delay to let the quantity input update
        setTimeout(() => {
          if (stickyQuantityInput instanceof HTMLInputElement && mainQuantityInput instanceof HTMLInputElement) {
            mainQuantityInput.value = stickyQuantityInput.value;
            mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, 10);
      };
      
      stickyQuantityButtons.forEach(button => {
        if (button instanceof HTMLElement && this.stickyQuantityButtonHandler) {
          button.addEventListener('click', this.stickyQuantityButtonHandler);
        }
      });
    }
    
    removeQuantityListeners() {
      const sectionId = this.dataset.sectionId;
      const mainQuantityInput = document.querySelector(`#Quantity-${sectionId}`);
      const stickyQuantitySelector = this.querySelector('quantity-selector-component');
      const stickyQuantityInput = stickyQuantitySelector ? stickyQuantitySelector.querySelector('input[type="number"]') : null;
      
      if (mainQuantityInput instanceof HTMLInputElement) {
        mainQuantityInput.removeEventListener('change', this.quantityChangeHandler);
      }
      
      if (stickyQuantityInput instanceof HTMLInputElement && this.stickyQuantityChangeHandler) {
        // Remove the change listener
        stickyQuantityInput.removeEventListener('change', this.stickyQuantityChangeHandler);
      }
      
      // Remove button listeners
      const stickyQuantityButtons = stickyQuantitySelector ? stickyQuantitySelector.querySelectorAll('button[name="minus"], button[name="plus"]') : [];
      stickyQuantityButtons.forEach(button => {
        if (button instanceof HTMLElement && this.stickyQuantityButtonHandler) {
          button.removeEventListener('click', this.stickyQuantityButtonHandler);
        }
      });
    }
  
    /**
     * @param {Event} event
     */
    handleQuantityChange(event) {
      const stickyQuantitySelector = this.querySelector('quantity-selector-component');
      const stickyQuantityInput = stickyQuantitySelector ? stickyQuantitySelector.querySelector('input[type="number"]') : null;
      
      if (stickyQuantityInput instanceof HTMLInputElement && event && event.target && event.target instanceof HTMLInputElement) {
        stickyQuantityInput.value = event.target.value;
      }
    }
  
    handleVariantChange() {
      // Update the sticky bar immediately and again after a short delay
      this.updateStickyBarVariant();
      
      setTimeout(() => {
        this.updateStickyBarVariant();
      }, 50);
    }
  
    updateStickyBarVariant() {
      const mainForm = document.querySelector('product-form form') ||
                      document.querySelector('form[data-type="add-to-cart-form"]');
      if (!mainForm) return;
      const variantIdInput = mainForm.querySelector('[name="id"]');
      if (!(variantIdInput instanceof HTMLInputElement)) return;
      
      // Update variant ID
      const stickyVariantIdInput = this.querySelector('[name="id"]');
      if (stickyVariantIdInput instanceof HTMLInputElement) {
        stickyVariantIdInput.value = variantIdInput.value;
      }
      
      this.updateVariantTitle();
      this.updateStickyBarPrice();
      this.updateAvailabilityStatus(mainForm);
    }
    
    updateVariantTitle() {
      const stickyFormVariantTitle = this.querySelector('.sticky-add-to-cart__variant');
      if (!stickyFormVariantTitle) return;
      
      // Reset previous content first
      stickyFormVariantTitle.textContent = '';
      
      // Try to get variant display from main product info
      const mainFormVariantTitle = document.querySelector('.product__variant-status');
      
      if (mainFormVariantTitle) {
        stickyFormVariantTitle.textContent = mainFormVariantTitle.textContent;
        return;
      }
      
      // Alternative: gather selected options
      /** @type {string[]} */
      const selectedOptions = [];
      
      // From dropdowns
      document.querySelectorAll('.product-form__input select').forEach(select => {
        if (select instanceof HTMLSelectElement && select.value) {
          selectedOptions.push(select.value);
        }
      });
      
      // From radio buttons
      document.querySelectorAll('.product-form__input input[type="radio"]:checked').forEach(radio => {
        if (radio instanceof HTMLInputElement) {
          selectedOptions.push(radio.value);
        }
      });
      
      // Display if we have options
      if (selectedOptions.length > 0) {
        const uniqueOptions = [...new Set(selectedOptions)];
        stickyFormVariantTitle.textContent = uniqueOptions.join(' / ');
      }
    }
    
    updateStickyBarPrice() {
      const mainPrice = document.querySelector('.price:not(.sticky-add-to-cart .price)');
      const stickyPrice = this.querySelector('.sticky-add-to-cart__price');
      
      if (mainPrice && stickyPrice) {
        // Clone price content to the sticky bar
        stickyPrice.innerHTML = '';
        const priceClone = mainPrice.cloneNode(true);
        stickyPrice.appendChild(priceClone);
      }
    }
    
    /**
     * @param {any} mainForm
     */
    updateAvailabilityStatus(mainForm) {
      const addToCartButton = mainForm.querySelector('.product-form__submit');
      const stickyAddToCartButton = this.querySelector('.product-form__submit');
      
      if (addToCartButton instanceof HTMLButtonElement && stickyAddToCartButton instanceof HTMLButtonElement) {
        // Update button state
        stickyAddToCartButton.disabled = addToCartButton.disabled;
        
        // Update button text
        const mainButtonText = addToCartButton.querySelector('span')?.textContent;
        const stickySpan = stickyAddToCartButton.querySelector('span');
        if (mainButtonText && stickySpan) {
          stickySpan.textContent = mainButtonText;
        }
      }
    }
  
    handleScroll() {
      const productForm = document.querySelector('product-form') || 
                          document.querySelector('.product-form');
      if (!(productForm instanceof HTMLElement)) return;
      
      const stickyHeight = this.offsetHeight;
      let shouldStick = false;
      
      // Different logic based on position (top or bottom)
      if (this.position === 'top') {
        // For top position, show when scrolled past the header
        const header = document.querySelector('#shopify-section-header');
        const headerHeight = header instanceof HTMLElement ? header.offsetHeight : 0;
        shouldStick = window.scrollY > headerHeight;
      } else {
        // For bottom position (default), show when scrolled past the product form
        shouldStick = window.scrollY > (productForm.offsetTop + productForm.offsetHeight);
      }
      
      const isSticky = this.classList.contains(this.stickyClass);
      
      // Add/remove sticky class based on scroll position
      if (shouldStick && !isSticky) {
        this.style.minHeight = `${stickyHeight}px`;
        this.classList.add(this.stickyClass);
      } else if (!shouldStick && isSticky) {
        this.style.removeProperty('min-height');
        this.classList.remove(this.stickyClass);
      }
    }
    
    /**
     * @param {Event} event
     */
    handleAddToCart(event) {
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      
      // Find main form
      const mainForm = document.querySelector('product-form form') ||
                      document.querySelector('form[data-type="add-to-cart-form"]');
      
      if (!mainForm) return;
      
      // Sync quantity and variant ID with main form
      this.syncFormData(mainForm);
      
      // Trigger main form submission
      const mainSubmitButton = mainForm.querySelector('[type="submit"]');
      if (mainSubmitButton instanceof HTMLElement && typeof mainSubmitButton.click === 'function') {
        mainSubmitButton.click();
      } else if (mainForm instanceof HTMLFormElement) {
        mainForm.submit();
      }
    }
    
    /**
     * @param {any} mainForm
     */
    syncFormData(mainForm) {
      const stickyQuantitySelector = this.querySelector('quantity-selector-component');
      const stickyQuantityInput = stickyQuantitySelector ? stickyQuantitySelector.querySelector('input[type="number"]') : null;
      const mainQuantityInput = document.querySelector(`#Quantity-${this.dataset.sectionId}`);
      
      if (stickyQuantityInput instanceof HTMLInputElement && mainQuantityInput instanceof HTMLInputElement) {
        mainQuantityInput.value = stickyQuantityInput.value;
        mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Sync variant ID
      const stickyVariantIdInput = this.querySelector('[name="id"]');
      const mainVariantIdInput = mainForm.querySelector('[name="id"]');
      
      if (stickyVariantIdInput instanceof HTMLInputElement && mainVariantIdInput instanceof HTMLInputElement) {
        mainVariantIdInput.value = stickyVariantIdInput.value;
      }
    }
  }
  
  customElements.define('sticky-add-to-cart', StickyAddToCart);
  
  // Handle header visibility changes
  window.addEventListener("scroll", function() {
    const stickyAddToCart = document.querySelector('sticky-add-to-cart');
    if (!stickyAddToCart) return;
    
    // Handle header state
    const header = document.querySelector('#shopify-section-header');
    const stickyInner = document.querySelector('.sticky-inner-container');
    
    if (header?.classList.contains('shopify-section-header-hidden')) {
      stickyInner?.classList.add('show-my-class');
    } else {
      stickyInner?.classList.remove('show-my-class');
    }
  }); 